import { Text, Tooltip } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { appTextModes } from "@/shared/ui/text";
import {
  adjacentSectionKey,
  type DatasetSection,
  type DatasetSectionDescriptor,
} from "../../feature/datasetSections";
import styles from "./DatasetSectionTabs.module.css";

type DatasetSectionTabsProps = {
  entries: readonly DatasetSectionDescriptor[];
  active: DatasetSection;
  onSelect: (key: DatasetSection) => void;
  /** 선택된 탭이 `aria-controls` 로 가리키는 상세 pane 의 id. 상세는 한 자리라 id 도 하나다. */
  panelId: string;
  /** 탭 id 접두 — 상세 pane 이 `aria-labelledby` 로 되짚는다. 창과 route 가 함께 떠도 안 겹친다. */
  idPrefix: string;
};

export function sectionTabId(idPrefix: string, key: string): string {
  return `${idPrefix}-tab-${key}`;
}

/**
 * 상세 섹션 탭 줄 — 창은 헤더 가운데(`stepHeader`), route 는 제목 줄 아래에 같은 줄을 세운다.
 *
 * 라벨만 든다. Probe 상태는 헤더의 배지가 말하고 섹션마다의 값은 각 섹션 본문이 말한다 — 탭 줄에
 * 요약을 실으면 네 섹션의 현황이 늘 같은 자리를 먹어 정작 보고 있는 섹션이 좁아진다.
 *
 * 클릭은 `onSelect` 로 나가고, 줄 안에 포커스가 있을 때의 ←/→ · Home/End 는 여기서 처리한다
 * (roving tabindex). 창 셸의 ←/→ 는 같은 `adjacentSectionKey` 를 써서 답이 갈리지 않는다.
 */
export function DatasetSectionTabs({
  entries,
  active,
  onSelect,
  panelId,
  idPrefix,
}: DatasetSectionTabsProps): ReactNode {
  const listRef = useRef<HTMLDivElement | null>(null);
  // 키로 옮긴 탭에 포커스가 이미 줄 안에 있을 때만 따라가게 한다 — 밖에서 눌렀는데 포커스를
  // 뺏으면 방금까지 보던 자리가 사라진다.
  const activeTabId = sectionTabId(idPrefix, active);
  useEffect(() => {
    const list = listRef.current;
    if (list === null || !list.contains(document.activeElement)) {
      return;
    }
    list.querySelector<HTMLElement>(`#${CSS.escape(activeTabId)}`)?.focus();
  }, [activeTabId]);

  const selectable = entries.filter((entry) => entry.disabledReason === null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : null;
    const edge =
      event.key === "Home" ? selectable.at(0) : event.key === "End" ? selectable.at(-1) : undefined;
    if (step === null && edge === undefined) {
      return;
    }
    event.preventDefault();
    const next = step === null ? (edge?.key ?? null) : adjacentSectionKey(entries, active, step);
    if (next !== null && next !== active) {
      onSelect(next);
    }
  };

  return (
    <div
      ref={listRef}
      className={styles.tabs}
      role="tablist"
      aria-label="Dataset 섹션"
      // 포커스 대상은 탭(roving tabindex)이고 줄 자체는 키 이벤트만 받는다.
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {entries.map((entry) => {
        const selected = entry.key === active;
        const locked = entry.disabledReason !== null;
        const tab = (
          <button
            key={entry.key}
            type="button"
            role="tab"
            id={sectionTabId(idPrefix, entry.key)}
            aria-selected={selected}
            aria-controls={panelId}
            // 잠긴 탭은 disabled 속성이 아니라 aria-disabled 다 — 포인터 이벤트가 살아 있어야 감싼
            // 툴팁 트리거가 hover 를 받아 사유를 말할 수 있다.
            aria-disabled={locked || undefined}
            tabIndex={selected ? 0 : -1}
            className={styles.tab}
            data-active={selected ? "true" : "false"}
            onClick={locked ? undefined : () => onSelect(entry.key)}
          >
            <Text variant={appTextModes["caption-bold"]}>{entry.label}</Text>
          </button>
        );
        if (entry.disabledReason === null) {
          return tab;
        }
        return (
          <Tooltip key={entry.key} content={entry.disabledReason} placement="bottom">
            {tab}
          </Tooltip>
        );
      })}
    </div>
  );
}
